import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiNavigation, FiGlobe, FiShare2 } = FiIcons;

const ResultsPanel = ({ originalLocation, antipodeLocation }) => {
  const getFunFact = (antipodeLocation) => {
    const { lat, lng, address } = antipodeLocation;
    
    if (address.toLowerCase().includes('ocean') || address.toLowerCase().includes('sea')) {
      return "🌊 You'd end up in the middle of the ocean! Most antipodes are in water since oceans cover 71% of Earth.";
    }
    
    if (Math.abs(lat) > 60) {
      return "🧊 Your antipode is in a polar region - pack some warm clothes for your journey through Earth!";
    }
    
    if (Math.abs(lat) < 23.5) {
      return "🌴 Lucky you! Your antipode is in the tropical zone where it's warm year-round.";
    }
    
    return "🌍 Fascinating! Your antipode represents the farthest point you could travel on Earth's surface.";
  };

  const getDistance = () => {
    // Distance through Earth's core is approximately 12,742 km (Earth's diameter)
    return "12,742 km (straight through Earth's core)";
  };

  const handleShare = async () => {
    const shareText = `I just discovered that if I dig straight through Earth from ${originalLocation.address}, I'd end up at ${antipodeLocation.address}! 🌍`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Dig Through Earth',
          text: shareText,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareText + ' ' + window.location.href);
        alert('Link copied to clipboard!');
      } catch (err) {
        console.log('Error copying to clipboard:', err);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <motion.h2
          className="text-3xl font-bold text-white flex items-center gap-3"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <SafeIcon icon={FiNavigation} className="text-blue-400" />
          Your Journey Results
        </motion.h2>
        
        <motion.button
          onClick={handleShare}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-4 py-2 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg flex items-center gap-2 hover:from-green-600 hover:to-blue-600 transition-all duration-300"
        >
          <SafeIcon icon={FiShare2} />
          Share
        </motion.button>
      </div>

      {/* Location Cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Original Location */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-400/30 rounded-xl p-6 glow-blue"
        >
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiMapPin} className="text-2xl text-blue-400" />
            <h3 className="text-xl font-semibold text-white">Starting Point</h3>
          </div>
          <p className="text-blue-100 mb-2 leading-relaxed">{originalLocation.address}</p>
          <p className="text-blue-300 font-mono text-sm">{originalLocation.coordinates}</p>
        </motion.div>

        {/* Antipode Location */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-green-500/20 to-green-600/20 border border-green-400/30 rounded-xl p-6 glow-green"
        >
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiGlobe} className="text-2xl text-green-400" />
            <h3 className="text-xl font-semibold text-white">Antipode (Other Side)</h3>
          </div>
          <p className="text-green-100 mb-2 leading-relaxed">{antipodeLocation.address}</p>
          <p className="text-green-300 font-mono text-sm">{antipodeLocation.coordinates}</p>
        </motion.div>
      </div>

      {/* Fun Facts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="space-y-4"
      >
        <div className="bg-purple-500/20 border border-purple-400/30 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            🎯 Fun Fact
          </h4>
          <p className="text-purple-100 leading-relaxed">
            {getFunFact(antipodeLocation)}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-orange-500/20 border border-orange-400/30 rounded-xl p-4">
            <h5 className="font-semibold text-white mb-2">🚀 Distance</h5>
            <p className="text-orange-100">{getDistance()}</p>
          </div>
          
          <div className="bg-pink-500/20 border border-pink-400/30 rounded-xl p-4">
            <h5 className="font-semibold text-white mb-2">⏱️ Journey Time</h5>
            <p className="text-pink-100">42 minutes (if you could fall through!)</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ResultsPanel;